import { ContentAudience } from "../../types/contentAudience";

// Mirrors the api's /niche contract. Money never appears here.
export type NicheArea = "clients" | "expertise" | "lifeStage" | "homeBase" | "serviceAreas" | "difference";

export interface NichePlace {
    town: string;
    state: string;
}

export interface NicheProfile {
    nicheStatement: string;
    audience: string;
    specialties: string[];
    homeBase: NichePlace;
    serviceAreas: NichePlace[];
    differentiators: string[];
}

export interface SavedNicheProfile extends NicheProfile {
    source: "interview" | "manual";
    savedAt: string;
}

export interface AllowanceStatus {
    percentUsed: number;
    paused: boolean;
    resetsOn: string | null;
}

// One question of the interview and, once given, its answer; the overview lists them in asked order.
export interface InterviewTurn {
    area: NicheArea;
    question: string;
    answer?: string;
}

export interface NicheOverview {
    eligible: boolean;
    profile: SavedNicheProfile | null;
    interview: {
        status: "none" | "in_progress" | "review";
        covered: number;
        total: number;
        // `turn` is sent back with the answer; optional because an older api doesn't send it.
        pendingQuestion: { area: NicheArea; text: string; turn?: number } | null;
        awaitingWriteUp: boolean;
        draftProfile: NicheProfile | null;
        // The conversation so far ([] when no interview is open); optional because an older api doesn't send it.
        turns?: InterviewTurn[];
    };
    allowance: AllowanceStatus;
}

export type InterviewReply =
    | { type: "question"; area: NicheArea; text: string; covered: number; total: number; turn?: number }
    | { type: "review"; draftProfile: NicheProfile };

export interface NicheLocalItem {
    title: string;
    date: string;
    town: string;
    sourceUrl: string;
    summary: string;
}

// A topic or script from before voices existed counts as Residential.
export const voiceOf = (x: { audience?: ContentAudience }): ContentAudience => x.audience ?? ContentAudience.Residential;

export interface NicheTopic {
    id: string;
    text: string;
    source: "niche" | "local" | "own";
    local?: NicheLocalItem & { whyItFits: string };
    audience?: ContentAudience; // the voice a niche or local topic was made for; absent means Residential
    createdAt: string;
    filmedAt?: string;
    postIds?: string[];
}

export interface NicheScript {
    topicId: string;
    text: string;
    estSeconds: number;
    createdAt: string;
    audience?: ContentAudience; // the voice it was written in; absent means Residential
}

export const EMPTY_PROFILE: NicheProfile = {
    nicheStatement: "",
    audience: "",
    specialties: [],
    homeBase: { town: "", state: "" },
    serviceAreas: [],
    differentiators: [],
};
